import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { userManagementAPI } from '../services/api';
import { User } from '../types';
import { Trash2, Check, X } from 'lucide-react';

const AdminUserManagementPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({
    totalUsers: 0,
    customers: 0,
    providers: 0,
    admins: 0,
  });

  // Selected user for role change
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newRole, setNewRole] = useState<'customer' | 'provider' | 'admin'>('customer');
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [showRoleChangeModal, setShowRoleChangeModal] = useState(false);

  // Selected user for password update
  const [passwordUpdateUser, setPasswordUpdateUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Account deletion requests
  const [deletionRequests, setDeletionRequests] = useState<Array<User & {
    accountDeletionRequestedAt?: string;
    accountDeletionReason?: string;
  }>>([]);
  const [showDeletionRequests, setShowDeletionRequests] = useState(false);
  const [loadingDeletionRequests, setLoadingDeletionRequests] = useState(false);

  // Available skills for providers
  const availableSkills = [
    { value: 'cleaning', label: 'Cleaning' },
    { value: 'plumbing', label: 'Plumbing' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'gardening', label: 'Gardening' },
    { value: 'painting', label: 'Painting' },
    { value: 'carpentry', label: 'Carpentry' },
    { value: 'driver', label: 'Driver' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchUsers();
  }, [searchTerm, roleFilter, currentPage]);

  const fetchDeletionRequests = async () => {
    try {
      setLoadingDeletionRequests(true);
      setError('');
      const response = await userManagementAPI.getDeletionRequests();
      if (response.success && response.data) {
        setDeletionRequests(response.data.requests);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch deletion requests');
    } finally {
      setLoadingDeletionRequests(false);
    }
  };

  const handleApproveDeletion = async (userId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to approve deletion for "${userName}"? This action is permanent and cannot be undone.`)) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      const response = await userManagementAPI.approveAccountDeletion(userId);
      if (response.success) {
        setSuccess(`Successfully approved and deleted account for ${userName}`);
        fetchDeletionRequests();
        fetchUsers(); // Refresh user list
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to approve deletion');
    }
  };

  const handleRejectDeletion = async (userId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to reject the deletion request for "${userName}"?`)) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      const response = await userManagementAPI.rejectAccountDeletion(userId);
      if (response.success) {
        setSuccess(`Successfully rejected deletion request for ${userName}`);
        fetchDeletionRequests();
        fetchUsers(); // Refresh user list
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reject deletion request');
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params: any = {
        page: currentPage,
        limit: 20,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (roleFilter) {
        params.role = roleFilter;
      }

      const response = await userManagementAPI.getAllUsers(params);
      
      if (response.success && response.data) {
        setUsers(response.data.users);
        setStats(response.data.stats);
        setTotalPages(response.data.pagination.totalPages);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async () => {
    if (!selectedUser) return;

    // Validate skills for provider role
    if (newRole === 'provider' && selectedSkills.length === 0) {
      setError('Please select at least one skill for the provider');
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      const response = await userManagementAPI.changeUserRole(
        selectedUser.id, 
        newRole,
        newRole === 'provider' ? selectedSkills : undefined
      );
      
      if (response.success) {
        setSuccess(`Successfully changed ${selectedUser.name}'s role to ${newRole}`);
        setShowRoleChangeModal(false);
        setSelectedUser(null);
        setSelectedSkills([]);
        fetchUsers();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to change user role');
    }
  };

  const openRoleChangeModal = (user: User) => {
    setSelectedUser(user);
    setNewRole(user.role as 'customer' | 'provider' | 'admin');
    setSelectedSkills(user.skills || []);
    setShowRoleChangeModal(true);
    setError('');
    setSuccess('');
  };

  const handleSkillToggle = (skill: string) => {
    setSelectedSkills(prev => {
      if (prev.includes(skill)) {
        return prev.filter(s => s !== skill);
      } else {
        return [...prev, skill];
      }
    });
  };

  const handleRoleSelectChange = (role: 'customer' | 'provider' | 'admin') => {
    setNewRole(role);
    // Clear skills when changing away from provider
    if (role !== 'provider') {
      setSelectedSkills([]);
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      const response = await userManagementAPI.deleteUser(userId);
      
      if (response.success) {
        setSuccess(`Successfully deleted user ${userName}`);
        fetchUsers();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleToggleBlacklist = async (userId: string, userName: string, isBlacklisted: boolean) => {
    const action = isBlacklisted ? 'blacklist' : 'remove from blacklist';
    if (!window.confirm(`Are you sure you want to ${action} user "${userName}"?`)) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      const response = await userManagementAPI.toggleBlacklist(userId, !isBlacklisted);
      
      if (response.success) {
        setSuccess(`Successfully ${action === 'blacklist' ? 'blacklisted' : 'removed from blacklist'} user ${userName}`);
        fetchUsers();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${action} user`);
    }
  };

  const openPasswordModal = (user: User) => {
    setPasswordUpdateUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswordModal(true);
    setError('');
    setSuccess('');
  };

  const handlePasswordUpdate = async () => {
    if (!passwordUpdateUser) return;

    // Validate password
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!window.confirm(`Are you sure you want to update the password for "${passwordUpdateUser.name}"?`)) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      const response = await userManagementAPI.updateUserPassword(passwordUpdateUser.id, newPassword);
      
      if (response.success) {
        setSuccess(`Successfully updated password for ${passwordUpdateUser.name}`);
        setShowPasswordModal(false);
        setPasswordUpdateUser(null);
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update password');
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      case 'provider':
        return 'bg-blue-100 text-blue-800';
      case 'customer':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-700">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
              <p className="mt-2 text-gray-600">Manage user accounts and roles</p>
            </div>
            <button
              onClick={() => {
                setShowDeletionRequests(!showDeletionRequests);
                if (!showDeletionRequests) {
                  fetchDeletionRequests();
                }
              }}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {showDeletionRequests ? 'Hide' : 'View'} Deletion Requests
              {deletionRequests.length > 0 && (
                <span className="ml-2 bg-red-700 text-white text-xs font-semibold px-2 py-1 rounded-full">
                  {deletionRequests.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Account Deletion Requests Section */}
        {showDeletionRequests && (
          <div className="mb-8 bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-red-50">
              <h2 className="text-xl font-semibold text-red-900 flex items-center">
                <Trash2 className="h-5 w-5 mr-2 text-red-600" />
                Pending Account Deletion Requests
              </h2>
            </div>
            {loadingDeletionRequests ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                <p className="mt-4 text-gray-700">Loading deletion requests...</p>
              </div>
            ) : deletionRequests.length === 0 ? (
              <div className="p-8 text-center text-gray-600">
                No pending deletion requests
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Requested At
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Reason
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {deletionRequests.map((request) => (
                      <tr key={request.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {request.avatar ? (
                              <img
                                className="h-10 w-10 rounded-full"
                                src={request.avatar}
                                alt={request.name}
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-red-500 flex items-center justify-center text-white font-semibold">
                                {request.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{request.name}</div>
                              <div className="text-sm text-gray-500 capitalize">{request.role}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{request.email}</div>
                          <div className="text-sm text-gray-500">{request.phone || 'No phone'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {request.accountDeletionRequestedAt 
                              ? new Date(request.accountDeletionRequestedAt).toLocaleString()
                              : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate">
                            {request.accountDeletionReason || 'No reason provided'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleApproveDeletion(request.id, request.name)}
                            className="text-green-600 hover:text-green-900 mr-4 flex items-center"
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleRejectDeletion(request.id, request.name)}
                            className="text-red-600 hover:text-red-900 flex items-center"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Reject
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-1">Total Users</div>
            <div className="text-3xl font-bold text-gray-900">{stats.totalUsers}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-1">Customers</div>
            <div className="text-3xl font-bold text-green-600">{stats.customers}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-1">Providers</div>
            <div className="text-3xl font-bold text-blue-600">{stats.providers}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-1">Admins</div>
            <div className="text-3xl font-bold text-purple-600">{stats.admins}</div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700">{success}</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Users
              </label>
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Role
              </label>
              <select
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Roles</option>
                <option value="customer">Customer</option>
                <option value="provider">Provider</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setRoleFilter('');
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-gray-600">
              No users found
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {user.avatar ? (
                              <img
                                className="h-10 w-10 rounded-full"
                                src={user.avatar}
                                alt={user.name}
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                                {user.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{user.name}</div>
                              <div className="text-sm text-gray-500">ID: {user.id ? user.id.slice(-8) : 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{user.email}</div>
                          <div className="text-sm text-gray-500">{user.phone || 'No phone'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1 flex-wrap">
                            {user.accountDeletionRequested ? (
                              <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800 items-center max-w-fit">
                                <Trash2 className="h-3 w-3 mr-1 flex-shrink-0" />
                                <span className="whitespace-nowrap">Deletion Requested</span>
                              </span>
                            ) : user.isBlacklisted ? (
                              <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 max-w-fit whitespace-nowrap">
                                Blacklisted
                              </span>
                            ) : (
                              user.isVerified ? (
                                <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 max-w-fit whitespace-nowrap">
                                  Verified
                                </span>
                              ) : (
                                <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 max-w-fit whitespace-nowrap">
                                  Unverified
                                </span>
                              )
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium">
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => openRoleChangeModal(user)}
                              disabled={user.id === currentUser?.id}
                              className="text-blue-600 hover:text-blue-900 disabled:text-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                              Change Role
                            </button>
                            <button
                              onClick={() => openPasswordModal(user)}
                              className="text-purple-600 hover:text-purple-900 whitespace-nowrap"
                            >
                              Update Password
                            </button>
                            <button
                              onClick={() => handleToggleBlacklist(user.id, user.name, user.isBlacklisted || false)}
                              disabled={user.id === currentUser?.id}
                              className={`disabled:text-gray-400 disabled:cursor-not-allowed whitespace-nowrap ${
                                user.isBlacklisted 
                                  ? 'text-green-600 hover:text-green-900' 
                                  : 'text-orange-600 hover:text-orange-900'
                              }`}
                            >
                              {user.isBlacklisted ? 'Unblacklist' : 'Blacklist'}
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id, user.name)}
                              disabled={user.id === currentUser?.id}
                              className="text-red-600 hover:text-red-900 disabled:text-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Page <span className="font-medium">{currentPage}</span> of{' '}
                        <span className="font-medium">{totalPages}</span>
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Role Change Modal */}
      {showRoleChangeModal && selectedUser && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowRoleChangeModal(false)}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Change User Role
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 mb-4">
                        Change role for <span className="font-semibold">{selectedUser.name}</span> ({selectedUser.email})
                      </p>
                      
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Current Role: <span className={`px-2 py-1 rounded ${getRoleBadgeColor(selectedUser.role)}`}>
                            {selectedUser.role}
                          </span>
                        </label>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          New Role
                        </label>
                        <select
                          value={newRole}
                          onChange={(e) => handleRoleSelectChange(e.target.value as 'customer' | 'provider' | 'admin')}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="customer">Customer</option>
                          <option value="provider">Provider</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>

                      {newRole === 'provider' && (
                        <>
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Select Skills <span className="text-red-500">*</span>
                            </label>
                            <div className="grid grid-cols-2 gap-2 p-3 border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
                              {availableSkills.map((skill) => (
                                <label
                                  key={skill.value}
                                  className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedSkills.includes(skill.value)}
                                    onChange={() => handleSkillToggle(skill.value)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                  />
                                  <span className="text-sm text-gray-700">{skill.label}</span>
                                </label>
                              ))}
                            </div>
                            {selectedSkills.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs text-gray-600">
                                  Selected: {selectedSkills.map(s => availableSkills.find(sk => sk.value === s)?.label).join(', ')}
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="mt-4 p-3 bg-blue-50 border-l-4 border-blue-500">
                            <p className="text-sm text-blue-700">
                              ℹ️ Changing to provider will initialize provider-specific fields (experience, hourly rate).
                            </p>
                          </div>
                        </>
                      )}

                      {newRole === 'customer' && (
                        <div className="mt-4 p-3 bg-green-50 border-l-4 border-green-500">
                          <p className="text-sm text-green-700">
                            ℹ️ Changing to customer will clear any provider-specific fields (skills, experience, hourly rate).
                          </p>
                        </div>
                      )}

                      {newRole === 'admin' && (
                        <div className="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-500">
                          <p className="text-sm text-yellow-700">
                            ⚠️ Admin users have full access to all system features. Provider-specific fields will be cleared. Use with caution.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleRoleChange}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Change Role
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRoleChangeModal(false);
                    setSelectedUser(null);
                    setSelectedSkills([]);
                    setError('');
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Update Modal */}
      {showPasswordModal && passwordUpdateUser && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowPasswordModal(false)}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Update User Password
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 mb-4">
                        Update password for <span className="font-semibold">{passwordUpdateUser.name}</span> ({passwordUpdateUser.email})
                      </p>
                      
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          New Password <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Enter new password (min 6 characters)"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                        <p className="mt-1 text-xs text-gray-500">Password must be at least 6 characters long</p>
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirm Password <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm new password"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>

                      <div className="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-500">
                        <p className="text-sm text-yellow-700">
                          ⚠️ This will immediately change the user's password. They will need to use the new password to log in.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handlePasswordUpdate}
                  disabled={!newPassword || !confirmPassword || newPassword.length < 6}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:bg-gray-400 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Update Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordUpdateUser(null);
                    setNewPassword('');
                    setConfirmPassword('');
                    setError('');
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserManagementPage;

